import IdentityStore from 'stores/IdentityStore';
import AccountStore from 'stores/AccountStore';
import I18nStore from 'stores/I18nStore';
import MetricsStore from 'stores/MetricsStore';

/* EelMetricsApi contains the logic to send
metrics for pageviews and user-actions to the Eel endpoint.
 Note: EelMetricsApi also acts as the API object
 for react-metrics, in addition to being called by
the MetricsStore for discrete user-actions. */

const eelMetricsState = {
  lastRouterPath: null,
  pageViewBuffer: [],
  pageViewTimeout: undefined,
};

const PROD_METRICS_URL = 'https://unagi-na.amazon.com/1/events/com.amazon.author.metrics.endpoint';
const DEV_METRICS_URL = 'https://unagi-na.amazon.com/1/events/com.amazon.author.metrics.test.endpoint';

const DEV_METRICS_LOCALSTORAGE_KEY = 'AmazonAuthorDevoMetricsEnabled';

const DATA_TYPE_AUDIT_EVENT = 'audit_event';
const DATA_TYPE_DETAIL = 'audit_event_detail';
const DATA_TYPE_PAGE_VIEW = 'page_view';

const EEL_VENDOR_NAME = 'EelLogger';

const PAGE_VIEW_POST_INTERVAL_MILLIS = 20000;

function isProd() {
  return process.env.NODE_ENV === 'production';
}

const EelMetricsApi = {
  reactMetricsConfig() {
    return {
      enabled: true,
      vendors: [{
        name: EEL_VENDOR_NAME,
        api: EelMetricsApi,
      }],
      pageDefaults: routeState => routeState,
    };
  },

  postMetricsDisabledMessage: 'Eel metrics are disabled. Ignoring request to post metrics',

  isEnabled() {
    if (isProd()) {
      return true;
    }

    return localStorage.getItem(DEV_METRICS_LOCALSTORAGE_KEY) !== null;
  },

  metricsUrl() {
    if (isProd()) {
      return PROD_METRICS_URL;
    }

    return DEV_METRICS_URL;
  },

  // lastRouterPath(), pageViewBuffer(), and clearPageViewBuffer() exist for testing
  lastRouterPath() {
    return eelMetricsState.lastRouterPath;
  },

  pageViewBuffer() {
    return eelMetricsState.pageViewBuffer.slice();
  },

  clearPageViewBuffer() {
    eelMetricsState.pageViewBuffer = [];
  },

  cancelPostPageViewsTimeout() {
    if (eelMetricsState.pageViewTimeout) {
      clearTimeout(eelMetricsState.pageViewTimeout);
    }
    eelMetricsState.pageViewTimeout = undefined;
  },

  createPostPageViewsTimeout(interval) {
    if (!eelMetricsState.pageViewTimeout) {
      eelMetricsState.pageViewTimeout = setTimeout(EelMetricsApi.postPageViews, interval);
    }
  },

  postPageViews() {
    const requestObj = EelMetricsApi
      .buildRequest(eelMetricsState.pageViewBuffer, () => {}, () => {});
    $.ajax(requestObj);

    EelMetricsApi.clearPageViewBuffer();
    eelMetricsState.pageViewTimeout = undefined;
  },

  // Create an ajax request for a set of events
  buildRequest(payload, onSuccess, onFailure) {
    const baseAjaxParams = {
      url: EelMetricsApi.metricsUrl(),
      method: 'POST',
      dataType: 'json',
      success: onSuccess,
      error: onFailure,
    };

    // Eel data format as defined at w?Sushi/User%20Docs/Eel
    const eelEvents = {
      events: payload,
    };

    return {
      ...baseAjaxParams,
      data: JSON.stringify(eelEvents),
    };
  },

  appendFailedEvents(arrayOfEvents) {
    const allEvents = arrayOfEvents.slice();

    MetricsStore.getState().failedEvents.forEach(
      (failedEvent) => {
        allEvents.push(failedEvent);
      },
    );

    return allEvents;
  },

  constructDetailEvents(anEvent) {
    const {
      details, eventName, timestamp, amazonAuthorId,
    } = anEvent;

    if (!details) {
      return [];
    }

    const newEvents = Object.entries(details).map(([key, value]) => {
      const detailEvent = {
        dataType: DATA_TYPE_DETAIL,
        detailName: key,
        detailValue: value,
        eventName,
        timestamp,
        amazonAuthorId,
      };

      return { data: detailEvent };
    });

    return newEvents;
  },

  // Make an ajax request to POST the given events to the Eel endpoint
  userAction(event) {
    if (EelMetricsApi.isEnabled()) {
      // Place a millisecond timestamp (UTC) and identity on the event data
      const newEvent = {
        ...event,
        timestamp: Date.now(),
        dataType: DATA_TYPE_AUDIT_EVENT,
      };

      const identityState = IdentityStore.getState();

      if (identityState.currentIdentity) {
        newEvent.customerId = identityState.currentIdentity.customerId;
        newEvent.amazonAuthorId = identityState.currentIdentity.amazonAuthorId;
      }

      const identifiedEvent = { data: newEvent };
      const eventsArray = [identifiedEvent];

      const detailEvents = EelMetricsApi.constructDetailEvents(newEvent);
      detailEvents.forEach((detailEvent) => {
        eventsArray.push(detailEvent);
      });

      const currentEvents = eventsArray;

      // append failed messages and recent page views to new event
      const allEvents = EelMetricsApi.appendFailedEvents(eventsArray);
      eelMetricsState.pageViewBuffer.forEach((pageViewData) => {
        allEvents.push(pageViewData);
      });

      EelMetricsApi.cancelPostPageViewsTimeout();

      return new Promise(((resolve, reject) => {
        const ajaxSuccess = () => {
          resolve(currentEvents);
          EelMetricsApi.clearPageViewBuffer();
        };

        const ajaxFailed = (xhr) => {
          const error = new Error(xhr.responseText);
          error.failedEvents = currentEvents;
          error.status = xhr.status;
          reject(error);
        };

        const requestObj = EelMetricsApi.buildRequest(allEvents, ajaxSuccess, ajaxFailed);
        $.ajax(requestObj);
      }));
    }
    return Promise.resolve(EelMetricsApi.postMetricsDisabledMessage);
  },

  // pageView is automatically called because EelMetricsApi is the react-metrics API object.
  pageView(eventName, routerState) {
    if (EelMetricsApi.isEnabled()) {
      const timeMillis = Date.now();
      const { customerId } = (AccountStore.getState().account) || {};
      const { amazonAuthorId, marketplace } = (IdentityStore.getState().currentIdentity) || {};

      const eelData = {
        data: {
          customerId,
          amazonAuthorId,
          timestamp: timeMillis,
          renderLocale: I18nStore.getState().currentLocale,
          route: routerState.pathname,
          fromRoute: eelMetricsState.lastRouterPath,
          dataType: DATA_TYPE_PAGE_VIEW,
          userAgent: navigator.userAgent,
          source: 'AuthorCentral',
          topLevelDomain: document.location.hostname,
          encryptedMarketplaceId: marketplace,
        },
      };

      eelMetricsState.lastRouterPath = routerState.pathname;
      EelMetricsApi.createPostPageViewsTimeout(PAGE_VIEW_POST_INTERVAL_MILLIS);

      eelMetricsState.pageViewBuffer.push(eelData);
    }
  },

  // react-metrics will throw if track() is undefined, but Amazon Author doesn't use it
  track() { },
};

export default EelMetricsApi;
